import type { CommentableType } from '../comment/comment.js'

/**
 * Notificação persistida. Transcrição de `Docs/02-Dominio/Notification.md`.
 *
 * **Existe como documento, não só como push**, porque o app tem central de
 * notificações, histórico e contador de não lidas — e porque um push que se
 * perde não pode levar a informação junto.
 *
 * ## Uma por destinatário
 *
 * Um evento gera **duas** `Notification` — uma por membro da casa — em vez de um
 * documento com lista de leitores. Assim `readAt` é um campo simples, o contador
 * de não lidas é uma contagem direta, e cada pessoa pode ter regras diferentes.
 * Com dois usuários, o custo de duplicar é irrelevante.
 *
 * ## `entityRef` não é opcional na prática
 *
 * *"Notificação que não navega para lugar nenhum é ruído, e ruído treina o
 * usuário a ignorar as próximas."* O tipo permite `null` só para avisos
 * genuinamente sistêmicos — `sync.failed` sem conta específica é o caso.
 *
 * O que o tipo não expressa:
 *
 * - **Notificação silenciada nem vira documento.** Quem decide é
 *   `notificationsFor`, antes de qualquer escrita — e é por isso que a decisão
 *   mora numa função pura, e não num `if` dentro do gatilho.
 * - `readAt` é o **único** campo que o cliente escreve. `firestore.rules` cobra
 *   isso desde a Fase 1, com `affectedKeys().hasOnly(['readAt'])`.
 */

export type NotificationType =
  | 'transaction.created'
  | 'transaction.large_amount'
  | 'invoice.closed'
  | 'invoice.due_soon'
  | 'receipt.awaiting_review'
  | 'product.price_drop'
  | 'reminder.due'
  | 'sync.failed'
  /**
   * Os DADOS do provider pararam de chegar, sem erro nenhum — o item responde,
   * mas a data dele não anda. Entrou em 2026-10-18, depois de uma semana assim
   * passar em silêncio. Ver `functions/src/pluggy/saude.ts`.
   */
  | 'sync.stale'
  /**
   * **Não está no esboço de `Notification.md`, e entrou por medição.**
   *
   * A nota da fase pedia `transaction.created`, uma por transação. O sync roda
   * uma vez por dia e cria as transações do dia **todas de uma vez** — na base
   * real, ~6 por manhã. Seis pushes às 06:00 é exatamente o que a mesma nota
   * avisa que *"treina a pessoa a desligar tudo"*. Decisão do Murilo: um resumo
   * por execução.
   *
   * `transaction.created` continua no catálogo porque ela volta a fazer sentido
   * quando houver lançamento em tempo real (webhook do provider, ou o FAB de
   * outra pessoa da casa) — aí a transação é **uma**, e o aviso é sobre ela.
   */
  | 'sync.summary'

export type DeliveryStatus = {
  status: 'pending' | 'sent' | 'failed' | 'skipped'
  /** Instante ISO 8601. */
  attemptedAt: string | null
  error: string | null
}

export type Notification = {
  id: string
  householdId: string
  /** Uma notificação por destinatário. Ver acima. */
  recipientUserId: string

  type: NotificationType
  title: string
  body: string

  /** Para onde tocar leva. `null` só em aviso sistêmico. */
  entityRef: {
    type: CommentableType | 'invoice' | 'listItem'
    id: string
  } | null

  /** Payload extra para o service worker. Texto puro, como o `Comment`. */
  data: Record<string, string>

  /** Instante ISO 8601, ou `null` enquanto não lida. */
  readAt: string | null

  delivery: {
    webPush: DeliveryStatus
    inApp: DeliveryStatus
  }

  /** Instante ISO 8601. */
  createdAt: string
}

/**
 * A preferência de um membro para um tipo.
 *
 * - `'push'` — notifica no app **e** manda push;
 * - `'inApp'` — só no app, sem push. É para o que a pessoa quer ver quando
 *   abrir, não quando acontecer;
 * - `'off'` — silenciado. **Nem vira documento**, e é essa a diferença que
 *   importa: um `readAt` automático deixaria o histórico cheio de coisa que
 *   ninguém pediu.
 */
export type NotificationChoice = 'push' | 'inApp' | 'off'

/**
 * O que cada membro escolheu, por tipo.
 *
 * **Documento único, e não uma coleção com um doc por tipo.** As preferências
 * são lidas **juntas** — o serviço precisa de todas para decidir uma
 * notificação — e escritas uma de cada vez por uma pessoa mexendo numa tela.
 * Uma coleção seria oito leituras para montar o mesmo mapa.
 *
 * **Tipo ausente vale o padrão**, e não "silenciado": um tipo novo tem de
 * chegar ligado, senão ele nasce invisível para quem nunca abriu a tela de
 * preferências. Ver `choiceFor`.
 */
export type NotificationSettings = {
  /** Parcial de propósito: o que não está aqui vale o padrão do tipo. */
  byType: Partial<Record<NotificationType, NotificationChoice>>
}

/**
 * O padrão de cada tipo, para quem nunca mexeu nas preferências.
 *
 * **`'push'` no que tem produtor, e `'inApp'` no resto.** Os tipos que ainda não
 * têm produtor entram discretos: quando o produtor nascer, ele não começa
 * mandando push para alguém que nunca soube que aquilo existia.
 *
 * `product.price_drop` fica em **`'inApp'`** mesmo com produtor: ele é uma
 * oportunidade, não um compromisso. Um push às 22:00 dizendo que o detergente
 * caiu 18% acorda alguém por um detergente — e quem quiser o push liga na tela
 * de preferências, que é onde a escolha mora.
 *
 * `reminder.due` **virou `'push'` na Fase 9**, junto com o produtor dele. Era
 * `'inApp'` pela regra acima, e ficou errado no instante em que o produtor
 * existiu: *"um lembrete com hora dispara push no horário"* é linha da
 * definição de pronto da fase, e um lembrete que só aparece quando a pessoa
 * abre o app não é um lembrete — é uma anotação. É também o único tipo em que a
 * pessoa **pediu** o aviso, item por item, escolhendo a hora.
 */
export const DEFAULT_CHOICE: Record<NotificationType, NotificationChoice> = {
  'sync.summary': 'push',
  'sync.failed': 'push',
  'sync.stale': 'push',
  'invoice.due_soon': 'push',
  'transaction.created': 'inApp',
  'transaction.large_amount': 'inApp',
  'invoice.closed': 'inApp',
  'receipt.awaiting_review': 'inApp',
  'product.price_drop': 'inApp',
  'reminder.due': 'push',
}

/** A escolha efetiva: o que o membro pediu, ou o padrão do tipo. */
export function choiceFor(
  settings: NotificationSettings | null,
  type: NotificationType,
): NotificationChoice {
  return settings?.byType[type] ?? DEFAULT_CHOICE[type]
}
